import { useCallback, useEffect, useRef, useState } from "react";

export interface UseInViewOptions {
  /** Keep the element revealed after its first entrance. */
  once?: boolean;
  /** Start the reveal slightly before the element reaches the viewport edge. */
  rootMargin?: string;
  /** Visible proportion required before entering. */
  threshold?: number;
  /** Bypass observation and expose content immediately. */
  disabled?: boolean;
}

export interface UseInViewResult<T extends Element> {
  inView: boolean;
  ref: (node: T | null) => void;
}

function supportsIntersectionObserver() {
  return (
    typeof window !== "undefined" && "IntersectionObserver" in window
  );
}

/**
 * Small IntersectionObserver hook with a visible-by-default fallback for old
 * browsers and reduced-motion mode.
 */
export function useInView<T extends Element>({
  once = true,
  rootMargin = "0px 0px -10% 0px",
  threshold = 0.12,
  disabled = false,
}: UseInViewOptions = {}): UseInViewResult<T> {
  const canObserve = !disabled && supportsIntersectionObserver();
  const [node, setNode] = useState<T | null>(null);
  const [inView, setInView] = useState(false);
  const hasEntered = useRef(false);

  const ref = useCallback((nextNode: T | null) => {
    setNode(nextNode);
  }, []);

  useEffect(() => {
    if (!canObserve || !node) {
      return;
    }

    if (once && hasEntered.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }

        if (entry.isIntersecting) {
          hasEntered.current = true;
          setInView(true);

          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [canObserve, node, once, rootMargin, threshold]);

  return { inView: !canObserve || inView, ref };
}
